import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { WithdrawalAdminService } from '../../_core/_services/withdrawal-admin.service';
import { NotificationService } from '../../_core/_services/notification.service';
import { PaymentGatewayProviderDto } from '../../_core/_model/payment-admin';

@Component({
  selector: 'app-settings',
  standalone: false,
  templateUrl: './settings.html',
  styleUrl: './settings.css',
})
export class Settings implements OnInit {
  syncingNigerianBanks = false;
  syncingGenericInstitutions = false;
  loadingProviders = false;

  providers: PaymentGatewayProviderDto[] = [];
  form!: FormGroup;

  constructor(
    private fb: FormBuilder,
    private withdrawalAdminService: WithdrawalAdminService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.loadProviders();
  }

  buildForm(): void {
    this.form = this.fb.group({
      countryCode: ['NG', [Validators.required, Validators.minLength(2), Validators.maxLength(10)]],
      providerCode: ['FLUTTERWAVE', [Validators.required]]
    });
  }

  loadProviders(): void {
    this.loadingProviders = true;

    this.withdrawalAdminService.getProviders().subscribe({
      next: (res) => {
        this.providers = (res.data || []).filter(x => x.isActive);
      },
      error: () => {
        this.providers = [];
        this.notificationService.showError('Failed to load payment providers.', 'Error');
      },
      complete: () => {
        this.loadingProviders = false;
      }
    });
  }

  syncNigerianBanks(): void {
    if (this.syncingNigerianBanks) return;

    this.syncingNigerianBanks = true;

    this.withdrawalAdminService.syncInstitutions('NG', 'FLUTTERWAVE').subscribe({
      next: (res) => {
        this.handleSyncSuccess(res, 'Nigerian banks synced successfully.');
      },
      error: (err) => {
        this.notificationService.showError(
          err?.error?.Error?.Details ||
          err?.error?.message ||
          'Failed to sync Nigerian banks.',
          'Error'
        );
      },
      complete: () => {
        this.syncingNigerianBanks = false;
      }
    });
  }

  syncInstitutions(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid || this.syncingGenericInstitutions) {
      return;
    }

    this.syncingGenericInstitutions = true;

    const countryCode = String(this.form.value.countryCode || '').trim().toUpperCase();
    const providerCode = String(this.form.value.providerCode || '').trim().toUpperCase();

    this.withdrawalAdminService.syncInstitutions(countryCode, providerCode).subscribe({
      next: (res) => {
        this.handleSyncSuccess(
          res,
          `Institutions synced successfully for ${countryCode} via ${providerCode}.`
        );
      },
      error: (err) => {
        this.notificationService.showError(
          err?.error?.Error?.Details ||
          err?.error?.message ||
          'Failed to sync institutions.',
          'Error'
        );
      },
      complete: () => {
        this.syncingGenericInstitutions = false;
      }
    });
  }

  private handleSyncSuccess(res: any, prefix: string): void {
    const data = res?.data;
    const created = data?.createdCount ?? 0;
    const updated = data?.updatedCount ?? 0;

    this.notificationService.showSuccess(
      `${prefix} Created: ${created}, Updated: ${updated}.`,
      'Success'
    );
  }

  isInvalid(controlName: string): boolean {
    const ctrl = this.form.get(controlName);
    return !!ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched);
  }
}